import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Section, Field, Money, DurumRozeti, grid } from '../bakim-onarim/DetayParcalari';
import { talepYeriLabel, odenekTuruLabel, durumLabel, ihaleOnayiLabel, fmtTarih, ekSatirlar, toplamOdenek, toplamHakedis, toTLString, yuzde, DURUM_TONE } from './finansOrtak';

function PdfIcon() {
  return (
    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function EkTablo({ baslik, satirlar, renk, displayMoney, testId }) {
  if (!satirlar.length) return null;
  return (
    <div data-testid={testId}>
      <p className={`text-xs font-medium mb-1.5 ${renk}`}>{baslik} ({satirlar.length})</p>
      <div className="overflow-x-auto rounded-lg border border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-gray-400 text-xs uppercase">
            <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Talep Yeri</th><th className="px-3 py-2 text-left">Tarih</th><th className="px-3 py-2 text-left">Yazı</th><th className="px-3 py-2 text-right">Tutar</th><th className="px-3 py-2 text-left">Durum</th></tr>
          </thead>
          <tbody>
            {satirlar.map((s, i) => (
              <tr key={i} className="border-t border-gray-800">
                <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                <td className="px-3 py-2 text-white">{talepYeriLabel(s.talep_yeri) || '-'}</td>
                <td className="px-3 py-2 text-white">{fmtTarih(s.tarih) || '-'}</td>
                <td className="px-3 py-2 text-white">{s.yazisi || '-'}</td>
                <td className="px-3 py-2 text-right text-white font-medium">{displayMoney(s.tutari || '') || '-'}</td>
                <td className="px-3 py-2"><DurumRozeti value={s.durumu} label={durumLabel(s.durumu)} toneMap={DURUM_TONE} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Finans › Ödenek kaydı bilgi kartı (İhaleler detay penceresiyle aynı düzen)
export function FinansOdenekDetailDialog({ open, onOpenChange, odenek: od, hakedisler = [], displayMoney, onEdit, onExportPDF, onOpenHakedis, canEdit = true }) {
  if (!od) return null;
  const taahhutler = ekSatirlar(od, 'taahhut');
  const protokoller = ekSatirlar(od, 'protokol');
  const bagli = hakedisler.filter(h => h.odenek_id === od.id);
  const toplam = toplamOdenek(od);
  const hakToplam = toplamHakedis(bagli);
  const kalan = toplam - hakToplam;
  const money = (n) => displayMoney(toTLString(n));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="finans-odenek-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-400 flex flex-wrap items-center gap-2" data-testid="finans-odenek-detail-title">
            Ödenek Detayları: {od.yil}
            <Badge className="bg-gray-800 text-gray-200 border-gray-700 text-[11px]">{odenekTuruLabel(od.odenek_turu)}</Badge>
          </DialogTitle>
          <DialogDescription className="text-gray-400" data-testid="finans-odenek-detail-isin-adi">{od.isin_adi || '-'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Section no="1" title="Temel Bilgiler" testId="finans-odenek-detail-section-1">
            <div className={grid}>
              <Field label="Yıl" value={od.yil} testId="finans-odenek-detail-yil" />
              <Field label="Talep Yeri" value={talepYeriLabel(od.talep_yeri)} testId="finans-odenek-detail-talep-yeri" />
              <Field label="Ödenek Türü" value={odenekTuruLabel(od.odenek_turu)} testId="finans-odenek-detail-odenek-turu" />
              <Field label="Alım Türü" value={od.alim_turu} testId="finans-odenek-detail-alim-turu" />
              <Field label="İşin Adı" value={od.isin_adi} className="col-span-2 md:col-span-3" testId="finans-odenek-detail-isin-adi-alan" />
            </div>
          </Section>

          <Section no="2" title="Talep ve Durum" testId="finans-odenek-detail-section-2">
            <div className={grid}>
              <Field label="Talep Tarihi" value={fmtTarih(od.talep_tarihi)} testId="finans-odenek-detail-talep-tarihi" />
              <Field label="Talep Yazısı Sayı/No" value={od.talep_yazisi_sayi_no} testId="finans-odenek-detail-talep-yazisi" />
              <div data-testid="finans-odenek-detail-durum"><span className="text-gray-500">Ödenek Durumu:</span> <DurumRozeti value={od.odenek_durumu} label={durumLabel(od.odenek_durumu)} toneMap={DURUM_TONE} testId="finans-odenek-detail-durum-rozet" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
              <Money label="Talep Tutarı (KDV Hariç)" value={displayMoney(od.talep_tutari)} testId="finans-odenek-detail-talep-tutari" tone="bg-emerald-900/20 border border-emerald-700/40" />
              <Money label="Toplam Ödenek (ek dahil, iptal hariç)" value={money(toplam)} testId="finans-odenek-detail-toplam" tone="bg-blue-900/20 border border-blue-700/40" />
            </div>
          </Section>

          <Section no="3" title="İhale Onayı" testId="finans-odenek-detail-section-3">
            <div className={grid}>
              <Field label="İhale Onayı" value={ihaleOnayiLabel(od.ihale_onayi)} testId="finans-odenek-detail-ihale-onayi" />
              <Field label="İhale Onayı Talep Tarihi" value={fmtTarih(od.ihale_onayi_talep_tarihi)} testId="finans-odenek-detail-ihale-onayi-tarih" />
              <Field label="İhale Onayı Talep Yazısı" value={od.ihale_onayi_talep_yazisi} testId="finans-odenek-detail-ihale-onayi-yazi" />
            </div>
          </Section>

          <Section no="4" title="Ek Taahhütler ve Ek Protokoller" testId="finans-odenek-detail-section-4">
            {!taahhutler.length && !protokoller.length ? (
              <p className="text-sm text-gray-500">Ek taahhüt / ek protokol kaydı yok.</p>
            ) : (
              <div className="space-y-3">
                <EkTablo baslik="Ek Taahhütler" satirlar={taahhutler} renk="text-yellow-300" displayMoney={displayMoney} testId="finans-odenek-detail-ek-taahhutler" />
                <EkTablo baslik="Ek Protokoller" satirlar={protokoller} renk="text-purple-300" displayMoney={displayMoney} testId="finans-odenek-detail-ek-protokoller" />
              </div>
            )}
          </Section>

          <Section no="5" title="Bağlı Hakedişler" testId="finans-odenek-detail-section-5">
            <div className="space-y-3">
              {bagli.length === 0 ? (
                <p className="text-sm text-gray-500">Bu ödeneğe bağlı hakediş yok.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-800">
                  <table className="w-full text-sm" data-testid="finans-odenek-detail-hakedis-tablosu">
                    <thead className="bg-gray-800 text-gray-400 text-xs uppercase">
                      <tr><th className="px-3 py-2 text-left">Hakediş No</th><th className="px-3 py-2 text-left">Talep Yeri</th><th className="px-3 py-2 text-left">Talep Tarihi</th><th className="px-3 py-2 text-right">Tutar</th><th className="px-3 py-2 text-left">Ödeme Durumu</th></tr>
                    </thead>
                    <tbody>
                      {bagli.map(h => (
                        <tr key={h.id} className="border-t border-gray-800" data-testid={`finans-odenek-detail-hakedis-${h.id}`}>
                          <td className="px-3 py-2 text-white">{h.hakedis_no || '-'}</td>
                          <td className="px-3 py-2 text-white">{talepYeriLabel(h.talep_yeri)}</td>
                          <td className="px-3 py-2 text-white">{fmtTarih(h.talep_tarihi) || '-'}</td>
                          <td className="px-3 py-2 text-right text-white font-medium">{displayMoney(h.talep_tutari) || '-'}</td>
                          <td className="px-3 py-2"><DurumRozeti value={h.odeme_durumu} label={durumLabel(h.odeme_durumu)} toneMap={DURUM_TONE} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
                <h4 className="text-blue-400 font-medium text-sm mb-2">Özet</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <Money label="Hakediş Sayısı" value={String(bagli.length)} testId="finans-odenek-detail-hakedis-sayisi" tone="bg-blue-900/30" />
                  <Money label="Toplam Hakediş" value={bagli.length ? money(hakToplam) : ''} testId="finans-odenek-detail-hakedis-toplam" tone="bg-blue-900/30" />
                  <Money label={`Kalan Ödenek${toplam > 0 ? ` (${yuzde(Math.max(kalan, 0), toplam)})` : ''}`} value={toplam > 0 ? `${kalan < 0 ? '-' : ''}${money(Math.abs(kalan))}` : ''} testId="finans-odenek-detail-kalan" tone={kalan < 0 ? 'bg-red-900/30' : 'bg-blue-900/30'} />
                </div>
              </div>
              {(od.last_modified_by_name || od.last_modified_at) && (
                <p className="text-xs text-gray-500" data-testid="finans-odenek-detail-son-duzenleyen">
                  Son düzenleyen: {od.last_modified_by_name || '-'}{od.last_modified_at ? ` · ${new Date(od.last_modified_at).toLocaleString('tr-TR')}` : ''}
                </p>
              )}
            </div>
          </Section>
        </div>

        <DialogFooter>
          {onOpenHakedis && (
            <Button onClick={() => onOpenHakedis(od)} variant="outline" className="border-gray-700 text-gray-200 hover:bg-gray-800 mr-auto" data-testid="finans-odenek-detail-hakedis-btn">Hakediş Sayfasında Aç</Button>
          )}
          {canEdit && (
            <Button onClick={() => { onOpenChange(false); onEdit(od); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="finans-odenek-detail-edit-btn">Düzenle</Button>
          )}
          <Button onClick={() => onExportPDF(od)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="finans-odenek-detail-pdf-btn"><PdfIcon />PDF İndir</Button>
          <Button onClick={() => onOpenChange(false)} className="bg-gray-800 text-white hover:bg-gray-700" data-testid="finans-odenek-detail-close-btn">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
